import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { toast } from "react-toastify";
import { mutate } from "swr";
import { v4 } from "uuid";
import { arrayDiff } from "@helpers/general";
import { diff } from "deep-object-diff";

import { store } from "@components/VerticalLayout/store";
import {
    store as InformationStore,
    setIsArchived,
    setCanValidate,
    setIsEdited,
} from "../../store";
import { generalConfigStore, setItemsDisable } from "@store";
import { clearData } from "@components/Common/MultiCustomCreatableSelection/store";
import { clearMultiCustomSelectionData } from "@components/Common/MultiCustomSelect/store";
import { clearMultiCustomSelectionData as clearMultiCustomSelection } from "@components/Common/MultiCustomSelectV2/store";
import { useFetchGlobal } from "../../../../../api/hooks";
import { GetSalesModesTypesAndTagsV2 } from "@pages/SettingGeneral/ModeOfSale/ModeOfSales/helper";
import { getDifferenceOfSaleMethodItemsInInformationMode } from "../../helper";
import {
    ID_SALE_SUPPORT_KIOSK,
    CC_WEBSITE_ID,
    CC_APP_ID,
    UBER_ID,
    DELIVEROO_ID,
    OTTER_ID,
    SCO_ID,
    languages,
    LANGUAGES,
    CLICK_AND_COLLECT_ID,
} from "../../../../../constants/index";

import { formReducer, initialFormState } from "./useInformationFormReducer";
import { ShowSuccessToast } from "@pages/SettingGeneral/Language/ShowSuccessToast";

const EXCLUDED_SUPPORTS = [
    CC_WEBSITE_ID,
    CC_APP_ID,
    UBER_ID,
    DELIVEROO_ID,
    OTTER_ID,
    SCO_ID,
] as const;

const EMPTY_ADN = {
    languages: LANGUAGES,
} as const;

const TOAST_ERROR_OPTIONS = {
    position: toast.POSITION.TOP_CENTER,
    autoClose: 2000,
    theme: "colored",
} as const;

interface UseInformationFormProps {
    dataInformation: any;
    isAddModeOfInformationClicked: boolean;
    isAddNewModeOfInformationButtonClicked: boolean;
    isConsult: boolean;
    dataIsUpdated?: boolean | number;
    dataTable?: any;
    setDataIsUpdated: React.Dispatch<React.SetStateAction<boolean | number>>;
    setIsAddModeOfInformationClicked: Function;
    setDataInformation: Function;
    setInputInformationMode: Function;
    setLimit: Function;
    setIsConsult: Function;
}

function useSharedContext() {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { userID, franchiseID, shopID, oneShop, operatorID } = useSnapshot(
        store
    );
    const { itemsDisable } = useSnapshot(generalConfigStore);
    const {
        modeInformationData,
        shopsModeInformationData,
        modeInformationFranchise,
        dataFranchiseArchive,
        canValidate,
        dataArchive,
    } = useSnapshot(InformationStore);

    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const uuidUser = localStorage.getItem("uuidUser");
    const shopId = oneShop ? shopID : "0";
    const selectedLanguage: any = useMemo(
        () => languages.find((el: any) => el.value === i18nextLng),
        [i18nextLng]
    );
    const apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/general/mode_of_Information`;

    return {
        t,
        fetchGlobal,
        userID,
        franchiseID,
        shopID,
        oneShop,
        operatorID,
        itemsDisable,
        modeInformationData,
        shopsModeInformationData,
        modeInformationFranchise,
        dataFranchiseArchive,
        canValidate,
        dataArchive,
        i18nextLng,
        uuidUser,
        shopId,
        selectedLanguage,
        apiUrl,
    };
}

function useClearSelections() {
    return useCallback(() => {
        clearData();
        clearMultiCustomSelectionData();
        clearMultiCustomSelection();
    }, []);
}

function useReferenceData(
    franchiseID: string,
    shopId: string,
    fetchGlobal: any,
    t: any,
    dataInformation: any
) {
    const [informationModes, setInformationModes] = useState<any[]>([]);
    const [supportOfSales, setSupportOfSales] = useState<any[]>([]);
    const [modeOfSales, setModeOfSales] = useState<any[]>([]);
    const [allModeOfSales, setAllModeOfSales] = useState<any[]>([]);
    const [tags, setTags] = useState<any[]>([]);
    const [language, setLanguage] = useState<string[]>([]);

    useEffect(() => {
        const loadReferenceData = async () => {
            try {
                const configs = await GetSalesModesTypesAndTagsV2(
                    franchiseID,
                    shopId,
                    fetchGlobal
                );
                if (!configs?.length) return;

                const data = configs[0];
                if (!data) return;

                const {
                    languages = [],
                    listModeOfInformation = [],
                    ListSaleSupport = [],
                    saleSupport = [],
                    modeOfSales = [],
                    tags = [],
                } = data;

                setLanguage(languages);
                setInformationModes(
                    listModeOfInformation.map((item: any) => ({
                        value: item.value,
                        label: t(item.label),
                    }))
                );

                const filteredSupports = saleSupport
                    .filter(
                        (item: any) =>
                            ListSaleSupport.includes(t(item.label)) &&
                            !EXCLUDED_SUPPORTS.includes(item.value)
                    )
                    .map((item: any) => ({
                        label: item.label,
                        value: item.value,
                    }));
                setSupportOfSales(filteredSupports);
                const translatedSaleModes = (modeOfSales || []).map(
                    (item: any) => ({
                        ...item,
                        designation: t(item.designation),
                    })
                );
                setAllModeOfSales(translatedSaleModes);
                setTags(tags);

                setModeOfSales([]);
            } catch (error) {
                console.error("Failed to load reference data");
            }
        };

        loadReferenceData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        informationModes,
        supportOfSales,
        modeOfSales,
        setModeOfSales,
        allModeOfSales,
        tags,
        language,
    };
}

function useFormValidation(form: any, dispatch: any, i18nextLng: string) {
    return useCallback((): boolean => {
        const errors = {
            selectName: false,
            inputInformationMode: false,
            inputModeOfSale: false,
            inputSupportOfSale: false,
        };
        let isValid = true;

        if ((form.advancedDisplayName?.languages?.[i18nextLng] ?? "") === "") {
            errors.selectName = true;
            isValid = false;
        }
        if (form.informationMode === "") {
            errors.inputInformationMode = true;
            isValid = false;
        }
        if (!form.modeOfSale || form.modeOfSale.length === 0) {
            errors.inputModeOfSale = true;
            isValid = false;
        }
        if (
            !form.supportOfSale ||
            Object.keys(form.supportOfSale).length === 0
        ) {
            errors.inputSupportOfSale = true;
            isValid = false;
        }
        if (form.isNameExist) isValid = false;

        dispatch({ type: "SET_VALIDATION", errors });
        return isValid;
    }, [form, dispatch, i18nextLng]);
}

function useExistenceChecks(
    form: any,
    t: any,
    modeInformationData: any,
    dataArchive: any,
    modeInformationFranchise: any,
    dataFranchiseArchive: any
) {
    const checkIfExistCombination = useCallback(() => {
        const baseData = modeInformationData?.data || [];
        const allData = dataArchive ? [...baseData, ...dataArchive] : baseData;
        return allData.some(
            (el: any) =>
                el[t("Mode of information")] === form.informationMode &&
                el["Sales_support"]?.label === form.supportOfSale?.label
        );
    }, [
        modeInformationData,
        dataArchive,
        t,
        form.informationMode,
        form.supportOfSale,
    ]);

    const checkFranchiseExist = useCallback(
        (archived = false) => {
            const source = archived
                ? dataFranchiseArchive
                : modeInformationFranchise;
            if (!source) return false;
            return source.some(
                (el: any) =>
                    el[t("Mode of information")] === form.informationMode &&
                    el["Sales_support"]?.label === form.supportOfSale?.label
            );
        },
        [
            modeInformationFranchise,
            dataFranchiseArchive,
            t,
            form.informationMode,
            form.supportOfSale,
        ]
    );

    return { checkIfExistCombination, checkFranchiseExist };
}

function useAssociatedShops(
    shopsModeInformationData: any,
    dataInformationId: any
) {
    return useCallback(() => {
        const result: any[] = [];
        const data = shopsModeInformationData?.data;
        if (!data?.length) return result;
        for (const element of data) {
            if (!element.subRows?.length) continue;
            for (const el of element.subRows) {
                if (el.Id === dataInformationId) result.push(el);
            }
        }
        return result;
    }, [shopsModeInformationData, dataInformationId]);
}

function useMOSFilter(allModeOfSales: any[], i18nextLng: string, t: any) {
    return useCallback(
        (supportSale: any, sale?: any) => {
            const modeSales = sale ?? allModeOfSales;
            if (!modeSales?.length) return [];
            return modeSales
                .filter(
                    (el: any) =>
                        el.support_vente?.includes(supportSale.label) &&
                        el.id !== CLICK_AND_COLLECT_ID
                )
                .map((el: any) => ({
                    label: t(
                        el.designation ||
                            el["Mode of sale"] ||
                            el.advancedDisplayName?.languages?.[i18nextLng]
                    ),
                    value: el.id,
                }));
        },
        [allModeOfSales, i18nextLng, t]
    );
}

function useSyncMOSOptions(
    supportOfSale: any,
    allModeOfSales: any[],
    getMOSFiltered: (supportSale: any, sale?: any) => any[],
    setModeOfSales: (value: any[]) => void
) {
    const supportLabel = supportOfSale?.label;
    const supportValue = supportOfSale?.value;

    useEffect(() => {
        if (allModeOfSales.length === 0 || !supportLabel) return;

        setModeOfSales(
            getMOSFiltered({ label: supportLabel, value: supportValue })
        );
    }, [
        supportLabel,
        supportValue,
        allModeOfSales,
        getMOSFiltered,
        setModeOfSales,
    ]);
}

function useSaveModeOfInformation(
    ctx: ReturnType<typeof useSharedContext>,
    form: any,
    dataInformation: any,
    checkFranchiseExist: (archived?: boolean) => boolean,
    getAssociatedShops: () => any[],
    resetForm: () => void,
    props: UseInformationFormProps,
    modalSetters: {
        setIsModalModifiedOpened: (v: boolean) => void;
        setIsModalOpen: (v: boolean) => void;
        setDataEdited: (v: any) => void;
        setKIOSKData: (v: any) => void;
        setIsSendingTag: (v: boolean) => void;
    }
) {
    const {
        t,
        fetchGlobal,
        userID,
        operatorID,
        uuidUser,
        franchiseID,
        oneShop,
        shopId,
        apiUrl,
        canValidate,
        itemsDisable,
    } = ctx;

    const {
        isAddModeOfInformationClicked,
        setIsAddModeOfInformationClicked,
        setDataIsUpdated,
    } = props;

    const {
        setIsModalModifiedOpened,
        setIsModalOpen,
        setDataEdited,
        setKIOSKData,
        setIsSendingTag,
    } = modalSetters;

    const buildBasePayload = useCallback(
        () => ({
            userId: userID,
            operatorId: operatorID,
            uuidUser,
            franchiseId: franchiseID,
        }),
        [userID, operatorID, uuidUser, franchiseID]
    );

    const showApiError = useCallback(() => {
        toast.error(t("There's an error")!, TOAST_ERROR_OPTIONS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveModeOfInformation = useCallback(async () => {
        const isEditMode = !!dataInformation?.Id;
        if (!canValidate) return;
        if (isEditMode && !dataInformation.Id) return;
        setCanValidate(false);

        if (isEditMode) toast.dismiss();

        const base = buildBasePayload();
        const entryId = isEditMode ? dataInformation.Id : v4();

        const sharedData = {
            designation: form.informationMode,
            idMI: form.informationModeId,
            supportOfSale: form.supportOfSale,
            salesSupportData: [form.supportOfSale],
            displayName: form.displayName,
            modeOfSale: form.modeOfSale,
            tag: form.tagsData,
            shopId,
            stateShop: true,
            advancedDisplayName: form.advancedDisplayName,
        };

        const entry = isEditMode
            ? {
                  ...sharedData,
                  isArchived: dataInformation.isArchived,
                  state: dataInformation.state,
              }
            : { ...sharedData, isArchived: false, state: false };

        const requestPayload = [
            {
                ...base,
                shopId: isEditMode
                    ? [shopId]
                    : oneShop
                    ? [shopId, 0]
                    : [shopId],
                ...(isEditMode ? {} : { adding: true }),
                data: { [entryId]: entry },
            },
        ];

        if (
            !isEditMode &&
            form.supportOfSale?.value === ID_SALE_SUPPORT_KIOSK &&
            oneShop
        ) {
            const alreadyDisabled = itemsDisable.some(
                (item: any) =>
                    item.id === form.informationModeId && item.shopId === shopId
            );
            if (!alreadyDisabled) {
                const newItems = [
                    ...itemsDisable,
                    { id: form.informationModeId, state: false, shopId },
                ];
                setItemsDisable(newItems);
                localStorage.setItem("itemsDisable", JSON.stringify(newItems));
            }
        }

        if (!isEditMode) {
            const isFranchiseExist = checkFranchiseExist(false);
            const isFranchiseArchived = checkFranchiseExist(true);

            if ((isFranchiseExist || isFranchiseArchived) && oneShop) {
                setIsModalModifiedOpened(true);
                setIsArchived(!isFranchiseExist);
                setCanValidate(true);
                return;
            }
        }

        let updatedKIOSKData: any = {};
        if (isEditMode && form.supportOfSale?.value === ID_SALE_SUPPORT_KIOSK) {
            updatedKIOSKData = getDifferenceOfSaleMethodItemsInInformationMode(
                {
                    modeOfSale: dataInformation[t("Sales method")],
                    advancedDisplayName: dataInformation.advancedDisplayName,
                },
                {
                    modeOfSale: [...form.modeOfSale],
                    advancedDisplayName: form.advancedDisplayName,
                }
            );
        }

        if (isEditMode) {
            setDataEdited(requestPayload[0]);
            const dataAssociate = oneShop ? [] : getAssociatedShops();

            if (dataAssociate.length > 0) {
                setKIOSKData(updatedKIOSKData);
                setIsModalOpen(true);
                setCanValidate(true);
                return;
            }
        }

        try {
            const body = isEditMode
                ? JSON.stringify({
                      dataConfig: requestPayload,
                      updatedKIOSKData,
                  })
                : JSON.stringify(requestPayload);

            const response: any = await fetchGlobal(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body,
            });

            if (!isEditMode) mutate(apiUrl, response);
            if (response.error) throw new Error("API returned error");

            if (isEditMode) {
                ShowSuccessToast(
                    t("The Mode of information is changed successfully")!
                );
                if (oneShop) {
                    setIsAddModeOfInformationClicked(
                        !isAddModeOfInformationClicked
                    );
                }
                setIsEdited(false);
            } else {
                ShowSuccessToast(t("Information mode is successfully added"));
                resetForm();
                props.setInputInformationMode(false);
            }

            setIsSendingTag(true);
            setDataIsUpdated(true);
        } catch {
            showApiError();
        }

        setCanValidate(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dataInformation,
        canValidate,
        buildBasePayload,
        form,
        shopId,
        oneShop,
        itemsDisable,
        checkFranchiseExist,
        setIsModalModifiedOpened,
        t,
        setDataEdited,
        getAssociatedShops,
        setKIOSKData,
        setIsModalOpen,
        fetchGlobal,
        apiUrl,
        setIsSendingTag,
        setDataIsUpdated,
        setIsAddModeOfInformationClicked,
        isAddModeOfInformationClicked,
        resetForm,
        showApiError,
    ]);

    return { saveModeOfInformation, showApiError };
}

function useFormSideEffects(
    form: any,
    dispatch: any,
    dataInformation: any,
    dataInformationId: any,
    isAddModeOfInformationClicked: boolean,
    isAddNewModeOfInformationButtonClicked: boolean,
    isConsult: boolean,
    t: any,
    i18nextLng: string,
    resetForm: () => void,
    clearAllSelections: () => void,
    checkIfExistCombination: () => boolean,
    getAssociatedShops: () => any[],
    setAssociated: (v: any[]) => void
) {
    useEffect(() => {
        resetForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAddModeOfInformationClicked]);

    useEffect(() => {
        clearAllSelections();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataInformationId]);

    useEffect(() => {
        if (dataInformationId) {
            dispatch({
                type: "SET_FIELDS",
                fields: {
                    title: t(
                        isConsult
                            ? "View a mode of information"
                            : "Modify a mode of information"
                    ),
                    informationMode: t(
                        dataInformation[t("Mode of information")]
                    ),
                    informationModeId: dataInformation.idMI,
                    tagsData: Array.isArray(dataInformation.tag)
                        ? dataInformation.tag
                        : [],
                    supportOfSale:
                        Object.keys(dataInformation["Sales_support"] || {})
                            .length > 0
                            ? dataInformation["Sales_support"]
                            : {},
                    modeOfSale: Array.isArray(
                        dataInformation[t("Sales method")]
                    )
                        ? dataInformation[t("Sales method")]
                        : [],
                    displayName:
                        dataInformation.advancedDisplayName?.languages?.[
                            i18nextLng
                        ] ?? "",
                    advancedDisplayName:
                        dataInformation.advancedDisplayName ?? EMPTY_ADN,
                    inputInformationMode: false,
                    inputSupportOfSale: false,
                    inputModeOfSale: false,
                    selectName: false,
                    isNameExist: false,
                },
            });
            setIsEdited(false);
            setCanValidate(true);
        } else {
            resetForm();
        }
        setAssociated(getAssociatedShops());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataInformationId, isAddNewModeOfInformationButtonClicked]);

    const currentInformationMode = form.informationMode;
    const currentSupportLabel = form.supportOfSale?.label;

    useEffect(() => {
        const exist = checkIfExistCombination();
        const isNew = dataInformationId === undefined;
        const originalMode = dataInformation[t("Mode of information")];
        const originalSupport = dataInformation["Sales_support"]?.label;
        const hasChanged =
            !isNew &&
            (originalMode !== currentInformationMode ||
                originalSupport !== currentSupportLabel);

        const shouldFlag =
            exist &&
            currentInformationMode != null &&
            ((isNew &&
                currentInformationMode !== "" &&
                currentSupportLabel != null &&
                currentSupportLabel !== "") ||
                hasChanged);

        dispatch({
            type: "SET_FIELD",
            field: "isNameExist",
            value: !!shouldFlag,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentInformationMode, currentSupportLabel, dataInformationId]);

    const currentAdvancedLang =
        form.advancedDisplayName?.languages?.[i18nextLng];

    useEffect(() => {
        if (currentAdvancedLang && currentAdvancedLang !== "") {
            dispatch({ type: "SET_FIELD", field: "selectName", value: false });
        }
    }, [currentAdvancedLang, dispatch]);
}

export function useInformationForm(props: UseInformationFormProps) {
    const {
        dataInformation,
        isAddModeOfInformationClicked,
        isAddNewModeOfInformationButtonClicked,
        isConsult,
    } = props;

    const ctx = useSharedContext();
    const {
        t,
        i18nextLng,
        shopId,
        oneShop,
        selectedLanguage,
        franchiseID,
        fetchGlobal,
    } = ctx;

    const clearAllSelections = useClearSelections();
    const [form, dispatch] = useReducer(formReducer, initialFormState);

    const [isSendingTag, setIsSendingTag] = useState(false);
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalModifiedOpened, setIsModalModifiedOpened] = useState(false);
    const [
        isAdvancedDisplayNameModalOpen,
        setIsAdvancedDisplayNameModalOpen,
    ] = useState(false);
    const [dataEdited, setDataEdited] = useState<any>();
    const [associated, setAssociated] = useState<any[]>([]);
    const [KIOSKData, setKIOSKData] = useState<any>({});

    const dataInformationId = dataInformation?.Id;

    const resetForm = useCallback(() => {
        dispatch({ type: "RESET" });
        dispatch({
            type: "SET_FIELD",
            field: "title",
            value: t("Add a Mode of information"),
        });
        clearAllSelections();
        setIsEdited(false);
        setCanValidate(true);
    }, [t, clearAllSelections]);

    const refData = useReferenceData(
        franchiseID,
        shopId,
        fetchGlobal,
        t,
        dataInformation
    );

    const getMOSFiltered = useMOSFilter(refData.allModeOfSales, i18nextLng, t);

    useSyncMOSOptions(
        form.supportOfSale,
        refData.allModeOfSales,
        getMOSFiltered,
        refData.setModeOfSales
    );

    const validateForm = useFormValidation(form, dispatch, i18nextLng);

    const { checkIfExistCombination, checkFranchiseExist } = useExistenceChecks(
        form,
        t,
        ctx.modeInformationData,
        ctx.dataArchive,
        ctx.modeInformationFranchise,
        ctx.dataFranchiseArchive
    );

    const getAssociatedShops = useAssociatedShops(
        ctx.shopsModeInformationData,
        dataInformationId
    );

    const { saveModeOfInformation } = useSaveModeOfInformation(
        ctx,
        form,
        dataInformation,
        checkFranchiseExist,
        getAssociatedShops,
        resetForm,
        props,
        {
            setIsModalModifiedOpened,
            setIsModalOpen,
            setDataEdited,
            setKIOSKData,
            setIsSendingTag,
        }
    );

    const handleValidate = useCallback(() => {
        if (!validateForm()) return;

        if (dataInformation?.Id !== undefined) {
            const original: any = ctx.modeInformationFranchise.find(
                (el: any) => el.Id === dataInformation.Id
            );

            if (original) {
                const tagDiff = arrayDiff(original.tag, form.tagsData);
                const SOSDiff = arrayDiff(
                    original["Sales_support"],
                    form.supportOfSale
                );
                const MOSDiff = diff(
                    original[t("Sales method")],
                    form.modeOfSale
                );

                const nothingChanged =
                    tagDiff.length === 0 &&
                    Object.keys(MOSDiff).length === 0 &&
                    SOSDiff.length === 0 &&
                    original[t("Mode of information")].toUpperCase() ===
                        form.informationMode.toUpperCase() &&
                    dataInformation.advancedDisplayName ===
                        form.advancedDisplayName;

                if (nothingChanged) {
                    setIsModalOpened(true);
                    setCanValidate(true);
                    return;
                }
            }
        }

        saveModeOfInformation();
    }, [
        validateForm,
        ctx.modeInformationFranchise,
        t,
        saveModeOfInformation,
        form,
        dataInformation,
    ]);

    useFormSideEffects(
        form,
        dispatch,
        dataInformation,
        dataInformationId,
        isAddModeOfInformationClicked,
        isAddNewModeOfInformationButtonClicked,
        isConsult,
        t,
        i18nextLng,
        resetForm,
        clearAllSelections,
        checkIfExistCombination,
        getAssociatedShops,
        setAssociated
    );

    return {
        form,
        dispatch,
        informationModes: refData.informationModes,
        supportOfSales: refData.supportOfSales,
        modeOfSales: refData.modeOfSales,
        tags: refData.tags,
        language: refData.language,
        isSendingTag,
        setIsSendingTag,
        isModalOpened,
        setIsModalOpened,
        isModalOpen,
        setIsModalOpen,
        isModalModifiedOpened,
        setIsModalModifiedOpened,
        isAdvancedDisplayNameModalOpen,
        setIsAdvancedDisplayNameModalOpen,
        dataEdited,
        associated,
        KIOSKData,
        handleValidate,
        clearAllSelections,
        i18nextLng,
        selectedLanguage,
        shopId,
        oneShop,
    };
}
